<template>
  <!-- POC Cuidados Paliativos (feature 16): perfil SUS do profissional.
       Usado no cadastro e na edição de profissional. O pai chama validate() e save(id). -->
  <fieldset class="cp-profile">
    <legend class="cp-profile-title">
      Cuidados paliativos
    </legend>
    <p class="cp-profile-hint">
      Dados usados na Produção SUS e na composição das equipes de cuidados paliativos.
    </p>

    <div class="cp-profile-field">
      <label
        class="cp-profile-label"
        :for="`cp-cns-${professionalId}`"
      >CNS do profissional</label>
      <input
        :id="`cp-cns-${professionalId}`"
        v-model.trim="form.cns"
        class="input"
        inputmode="numeric"
        maxlength="15"
        placeholder="15 dígitos"
      >
      <p
        v-if="showErrors && errors.cns"
        class="cp-profile-error"
      >
        {{ errors.cns }}
      </p>
    </div>

    <div class="cp-profile-field">
      <span class="cp-profile-label">Certificação em cuidados paliativos</span>
      <div class="cp-profile-row">
        <div class="select">
          <select
            v-model="form.certificateType"
            aria-label="Tipo de certificação"
          >
            <option value="">
              Sem certificação
            </option>
            <option
              v-for="option in certificateOptions"
              :key="option._id"
              :value="option._id"
            >
              {{ option.name }}
            </option>
          </select>
        </div>
        <input
          v-model.number="form.certificateHours"
          class="input cp-profile-hours"
          type="number"
          min="1"
          aria-label="Carga horária da certificação"
          placeholder="Horas"
          :disabled="!form.certificateType"
        >
      </div>
      <p
        v-if="showErrors && errors.certificate"
        class="cp-profile-error"
      >
        {{ errors.certificate }}
      </p>
    </div>

    <div class="cp-profile-field">
      <span class="cp-profile-label">Atuação por equipe</span>
      <p
        v-if="teams.length === 0"
        class="cp-profile-muted"
      >
        Selecione as equipes do profissional acima para informar o CBO e a carga horária em cada uma.
      </p>
      <table
        v-else
        class="cp-profile-table"
      >
        <thead>
          <tr>
            <th>Equipe</th>
            <th>CBO</th>
            <th>Horas/semana</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="team in teams"
            :key="team.id"
          >
            <td class="cp-profile-team">
              {{ team.label }}
            </td>
            <td>
              <div class="select is-small">
                <select
                  v-model="unitForm(team.id).cbo"
                  :aria-label="`CBO em ${team.label}`"
                >
                  <option value="">
                    Não informado
                  </option>
                  <option
                    v-for="(label, code) in cboLabels"
                    :key="code"
                    :value="code"
                  >
                    {{ code }} · {{ label }}
                  </option>
                </select>
              </div>
            </td>
            <td>
              <input
                v-model.number="unitForm(team.id).weeklyHours"
                class="input is-small cp-profile-hours"
                type="number"
                min="1"
                max="60"
                :aria-label="`Horas por semana em ${team.label}`"
              >
            </td>
          </tr>
        </tbody>
      </table>
      <p
        v-if="teams.length > 0"
        class="cp-profile-muted"
      >
        Total: {{ totalHours }}h por semana. A PNCP exige ao menos 20h na equipe e no máximo 60h somando todas as equipes de cuidados paliativos.
      </p>
      <p
        v-if="showErrors && errors.units"
        class="cp-profile-error"
      >
        {{ errors.units }}
      </p>
    </div>
  </fieldset>
</template>

<script>
import { CBO_LABELS } from "@/services/palliative/susProductionApi";
import {
  CERTIFICATE_OPTIONS,
  emptyProfessionalProfile,
  getProfessionalProfile,
  saveProfessionalProfile
} from "@/services/palliative/professionalProfileApi";

export default {
  name: "PalliativeProfessionalProfile",
  props: {
    // Vazio no cadastro: o id só existe depois do POST, e o pai o passa para save(id).
    professionalId: {
      type: [String, Number],
      default: null
    },
    // Equipes marcadas no formulário: [{ id, label }].
    teams: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      form: emptyProfessionalProfile(),
      showErrors: false,
      certificateOptions: CERTIFICATE_OPTIONS,
      cboLabels: CBO_LABELS
    };
  },
  computed: {
    totalHours() {
      return this.teams.reduce((sum, team) => sum + (Number(this.unitForm(team.id).weeklyHours) || 0), 0);
    },
    errors() {
      const errors = {};
      if (this.form.cns && !/^\d{15}$/.test(this.form.cns)) {
        errors.cns = "O CNS deve ter 15 dígitos.";
      }
      const certificateHours = Number(this.form.certificateHours);
      if (this.form.certificateType && (!Number.isInteger(certificateHours) || certificateHours < 1)) {
        errors.certificate = "Informe a carga horária da certificação.";
      }
      const invalidHours = this.teams.some((team) => {
        const hours = this.unitForm(team.id).weeklyHours;
        if (hours === "" || hours === null || hours === undefined) return false;
        return !Number.isInteger(Number(hours)) || hours < 1 || hours > 60;
      });
      if (invalidHours) {
        errors.units = "A carga horária de cada equipe deve ficar entre 1 e 60 horas.";
      } else if (this.totalHours > 60) {
        errors.units = "A soma das cargas horárias passa de 60 horas por semana.";
      }
      return errors;
    }
  },
  watch: {
    // Equipe marcada depois do carregamento ganha uma linha vazia.
    teams: {
      immediate: true,
      handler(teams) {
        teams.forEach((team) => this.unitForm(team.id));
      }
    }
  },
  created() {
    if (this.professionalId) this.load();
  },
  methods: {
    async load() {
      try {
        const profile = await getProfessionalProfile(this.professionalId);
        // Mantém as linhas das equipes já marcadas na tela.
        this.form = { ...profile, units: { ...this.form.units, ...profile.units } };
      } catch (error) {
        console.error("POC Cuidados Paliativos: não foi possível carregar o perfil do profissional.", error);
      }
    },
    unitForm(unitId) {
      const key = String(unitId);
      if (!this.form.units[key]) {
        this.form.units[key] = { cbo: "", weeklyHours: "" };
      }
      return this.form.units[key];
    },
    validate() {
      this.showErrors = true;
      return Object.keys(this.errors).length === 0;
    },
    // Envia só as equipes marcadas (vínculos atuais). Equipes desmarcadas não são apagadas
    // na API: se o vínculo voltar, o CBO e as horas voltam junto. Lança erro se a API recusar.
    async save(professionalId = this.professionalId) {
      if (!professionalId) return;
      await saveProfessionalProfile(professionalId, this.form, this.teams.map((team) => team.id));
    }
  }
};
</script>

<style lang="scss" scoped>
$primary: #4f3f82;
$muted: #6b6b6b;
$critical: #c0262d;

.cp-profile {
  margin: 8px 0 22px;
  padding: 18px 20px;
  border: 1px solid #dcd8e6;
  border-radius: 12px;
}

.cp-profile-title {
  padding: 0 6px;
  font-weight: 700;
  color: $primary;
}

.cp-profile-hint,
.cp-profile-muted {
  font-size: 13px;
  color: $muted;
}

.cp-profile-hint {
  margin-bottom: 14px;
}

.cp-profile-muted {
  margin-top: 8px;
}

.cp-profile-field {
  margin-bottom: 16px;
}

.cp-profile-label {
  display: block;
  font-size: 13px;
  color: $muted;
  margin-bottom: 8px;
}

.cp-profile-row {
  display: flex;
  gap: 10px;
}

.cp-profile-hours {
  width: 90px;
}

.cp-profile-table {
  width: 100%;
  font-size: 13px;

  th {
    padding: 4px 6px;
    text-align: left;
    font-weight: 600;
    color: $muted;
  }

  td {
    padding: 4px 6px;
    vertical-align: middle;
  }
}

.cp-profile-team {
  width: 45%;
}

.cp-profile-error {
  margin-top: 6px;
  font-size: 12px;
  color: $critical;
}
</style>
